import type { MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { CloseBold16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import tinycolor from 'tinycolor2';

export interface PopupCloseControlProps {
  className?: string;
  backgroundColor?: string;
  color?: string;
  onClick?: (event: MouseEvent) => void;
  disabled?: boolean;
}

export function PopupCloseControl({
  className,
  backgroundColor,
  color = 'rgb(255, 255, 255)',
  disabled,
  onClick
}: PopupCloseControlProps) {
  const invertedColor = getInvertedColor(color);
  const derivedBackgroundColor =
    backgroundColor ?? tinycolor.mix(invertedColor, color, 10).toString();

  return (
    <PopupCloseControlContainer
      className={className}
      aria-label="Close"
      onClick={onClick}
      disabled={disabled}
      _color={color}
      _backgroundColor={derivedBackgroundColor}
    >
      <CloseIcon component={CloseBold16} size={16} />
    </PopupCloseControlContainer>
  );
}

export const PopupCloseControlContainer = styled.button<{
  _color: string;
  _backgroundColor: string;
}>`
  ${({ disabled, _color, _backgroundColor }) => css`
    position: relative;
    display: block;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    -webkit-tap-highlight-color: transparent;
    background-color: ${_backgroundColor};
    color: rgb(from ${_color} r g b / ${disabled ? 30 : 70}%);
    transition: color 200ms ease-out;

    ${focusVisibleMixin()}

    ${!disabled &&
    css`
      cursor: pointer;

      &:hover,
      &:focus-within,
      &:active {
        color: ${_color};
      }
    `}
  `}
`;

const CloseIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  & svg {
    fill: currentColor;
  }
`;
